import { React, useState } from 'react';

import { Box, Button, TextField } from '@mui/material';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import {
  AttentionBox,
  ChoiceChips,
  PageIntro,
  Panel,
  SummaryItem,
  SummaryList,
  TwoColumnLayout
} from '../../components/ui';
import { formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCep, isCompleteCep } from '../../utils/stringFormat';

const occurrenceOptions = [
  ['altaTensao', 'Alta Tensão'],
  ['crefaz', 'Crefaz'],
  ['parcelamento', 'Parcelamento'],
  ['baixaRenda', 'Baixa Renda'],
  ['ilegivel', 'Ilegível'],
  ['importe', 'Importe'],
  ['incompleta', 'Incompleta'],
  ['comprovante', 'Comprovante']
];

const ValidarFatura = () => {
  const initialFormState = {
    telefone: '',
    titular: '',
    cc: '',
    instalacao: '',
    consumoMedio: 0,
    dateTime: null,
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    altaTensao: false,
    crefaz: false,
    baixaRenda: false,
    ilegivel: false,
    parcelamento: false,
    importe: false,
    incompleta: false,
    comprovante: false
  };

  const simpleForm = {
    titular: 't',
    cc: '1',
    instalacao: 1,
    dateTime: null,
    rua: 'R',
    numero: '1',
    complemento: 'C',
    bairro: 'B',
    cidade: 'C',
    estado: 'Pernambuco',
    cep: '1'
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const [form, setForm] = useState(initialFormState);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [openAlerta, setOpenAlerta] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  const today = new Date();
  const minDate = new Date(today.getFullYear(), today.getMonth() - 7, 1);
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  const handleChangeSimple = () => {
    setForm(prev => ({
      ...prev,
      ...simpleForm
    }));
  };

  const hideForm = form.importe || form.ilegivel || form.baixaRenda || form.crefaz || form.altaTensao || form.incompleta || form.comprovante || form.parcelamento;
  const selectedOccurrences = occurrenceOptions.filter(([key]) => form[key]).map(([, label]) => label);
  const completedFields = [form.telefone, hideForm || form.titular, hideForm || form.cc].filter(Boolean).length;

  async function getAddressData() {
    const body = JSON.stringify({ cep: form.cep });

    const response = await fetchFlask('buscarDadosCEP', body);

    if (response.status === 'success') {
      setForm(prev => ({
        ...prev,
        rua: response.data.logradouro,
        bairro: response.data.bairro,
        cidade: response.data.localidade,
        estado: response.data.estado
      }));
    } else {
      setMessage(response.message);
      setStatus(response.status);
      setOpenAlerta(true);
    }
  }

  async function submitValidarFatura(e) {
    e.preventDefault();
    setIsSubmit(true);

    const periodo = form.dateTime ? formatIsoDate(form.dateTime) : '';

    const body = JSON.stringify({
      codigo_do_cliente: form.cc,
      titular: form.titular,
      telefone: form.telefone,
      instalacao: form.instalacao,
      periodo,
      consumo_medio: form.consumoMedio,
      endereco: {
        rua: form.rua,
        numero: form.numero,
        bairro: form.bairro,
        complemento: form.complemento,
        cidade: form.cidade,
        estado: form.estado,
        cep: form.cep
      },
      crefaz: form.crefaz,
      ilegivel: form.ilegivel,
      importe: form.importe,
      alta_tensao: form.altaTensao,
      baixa_renda: form.baixaRenda,
      incompleta: form.incompleta,
      parcelamento: form.parcelamento,
      comprovante: form.comprovante
    });

    const response = await fetchFlask('chatgpt/validarFatura', body);

    setMessage(response.message);
    setStatus(response.status);
    setOpenAlerta(true);
    setIsSubmit(false);
    setForm(initialFormState);
  }

  const summary = (
    <Panel eyebrow='Resumo' icon={FactCheckRoundedIcon} title='Revisão da validação'>
      <SummaryList>
        <SummaryItem label='Telefone' empty={!form.telefone} value={form.telefone || 'Não informado'} />
        <SummaryItem
          label='Ocorrências'
          empty={selectedOccurrences.length === 0}
          value={selectedOccurrences.length === 0 ? 'Nenhuma marcada' : selectedOccurrences.join(', ')}
        />
        <SummaryItem
          label='Titular'
          empty={!hideForm && !form.titular}
          value={hideForm ? 'Preenchido automaticamente' : (form.titular || 'Não informado')}
        />
        <SummaryItem
          label='Período'
          empty={!hideForm && !form.dateTime}
          value={hideForm ? 'Preenchido automaticamente' : (form.dateTime ? formatIsoDate(form.dateTime) : 'Não informado')}
        />
      </SummaryList>

      <AttentionBox icon={ReportProblemRoundedIcon}>
        {hideForm
          ? 'Com uma ocorrência marcada, os dados da fatura e o endereço são preenchidos automaticamente.'
          : 'Confira telefone, dados da fatura e endereço antes de enviar para validação.'}
      </AttentionBox>

      <AttentionBox icon={ChecklistRoundedIcon} title={`${completedFields}/3 preenchidos`} tone={completedFields === 3 ? 'ok' : 'navy'}>
        Campos críticos
      </AttentionBox>

      <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
        Validar Fatura
      </SubmitButton>
    </Panel>
  );

  return (
    <Box className='mainBox ui-workflow-page'>
      <Alerta
        texto={message}
        status={status}
        open={openAlerta}
        handleClose={() => setOpenAlerta(false)}
      />
      <PageHeader>Validação de Fatura</PageHeader>

      <PageIntro eyebrow='Atendimento IA' title='Validação de fatura'>
        Informe o telefone, marque as ocorrências e preencha os dados da fatura para enviar à validação.
      </PageIntro>

      <TwoColumnLayout as='form' aside={summary} onSubmit={submitValidarFatura}>
        <Panel className='is-surface' eyebrow='Etapa 1' icon={PhoneRoundedIcon} title='Contato'>
          <div className='ui-fields-grid'>
            <TextField
              required
              label='Telefone'
              value={form.telefone}
              onChange={(e) => handleChange('telefone', e.target.value)}
            />
          </div>
        </Panel>

        <Panel className='is-surface' eyebrow='Etapa 2' icon={ReportProblemRoundedIcon} title='Ocorrências'>
          <ChoiceChips
            ariaLabel='Ocorrências da fatura'
            options={occurrenceOptions.map(([value, label]) => ({ value, label }))}
            values={form}
            onToggle={(key, checked) => {
              handleChange(key, checked);
              handleChangeSimple();
            }}
          />
        </Panel>

        {!hideForm && (
          <>
            <Panel className='is-surface' eyebrow='Etapa 3' icon={ReceiptLongRoundedIcon} title='Dados da fatura'>
              <div className='ui-fields-grid is-three'>
                <TextField
                  label='Titular'
                  value={form.titular}
                  onChange={(e) => handleChange('titular', e.target.value)}
                />
                <TextField
                  label='Conta Contrato'
                  value={form.cc}
                  onChange={(e) => handleChange('cc', e.target.value)}
                />
                <TextField
                  label='Instalação'
                  value={form.instalacao}
                  onChange={(e) => handleChange('instalacao', e.target.value)}
                />
                <TextField
                  label='Consumo Médio'
                  type='number'
                  value={form.consumoMedio}
                  onChange={(e) => handleChange('consumoMedio', e.target.value)}
                />
                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label='Período'
                    views={['month', 'year']}
                    value={form.dateTime}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(value) => handleChange('dateTime', value)}
                  />
                </LocalizationProvider>
              </div>
            </Panel>

            <Panel className='is-surface' eyebrow='Etapa 4' icon={PlaceRoundedIcon} title='Endereço'>
              <div className='ui-lookup-grid'>
                <TextField
                  label='CEP'
                  inputProps={{ inputMode: 'numeric' }}
                  placeholder='00000-000'
                  value={form.cep}
                  onChange={(e) => handleChange('cep', formatCep(e.target.value))}
                />
                <Button variant='contained' startIcon={<SearchRoundedIcon />} disabled={!isCompleteCep(form.cep)} onClick={getAddressData}>
                  Buscar CEP
                </Button>
              </div>
              <div className='ui-fields-grid is-three'>
                <TextField
                  className='is-wide'
                  label='Rua'
                  value={form.rua}
                  onChange={(e) => handleChange('rua', e.target.value)}
                />
                <TextField
                  label='Número'
                  value={form.numero}
                  onChange={(e) => handleChange('numero', e.target.value)}
                />
                <TextField
                  label='Complemento'
                  value={form.complemento}
                  onChange={(e) => handleChange('complemento', e.target.value)}
                />
                <TextField
                  label='Bairro'
                  value={form.bairro}
                  onChange={(e) => handleChange('bairro', e.target.value)}
                />
                <TextField
                  label='Cidade'
                  value={form.cidade}
                  onChange={(e) => handleChange('cidade', e.target.value)}
                />
                <TextField
                  label='Estado'
                  value={form.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                />
              </div>
            </Panel>
          </>
        )}
      </TwoColumnLayout>
    </Box>
  );
};

export default ValidarFatura;
